import React, { useState, useEffect, useCallback } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Play,
  FileText
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getUserMeetingSession } from "../../../services/meetingServices";

const Sessions = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSessions, setTotalSessions] = useState(0);

  // Fetch sessions
  const getSessions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getUserMeetingSession();
      const sessionsData = response.data || [];
      setSessions(sessionsData);
      setTotalSessions(sessionsData.length);
      setTotalPages(Math.ceil(sessionsData.length / rowsPerPage));
    } catch (err) {
      console.error("Error fetching sessions:", err);
      toast.error("Failed to load sessions");
      setSessions([]);
      setTotalSessions(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [rowsPerPage]);

  useEffect(() => {
    getSessions();
  }, [getSessions]);

  const handleSessionClick = (session) => {
    navigate(`/dashboard/ai-notetaker/${session.sessionId}`);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleReplayClick = (session) => {
    if (session.recordingId) {
      navigate(`/dashboard/recordings/${session.recordingId}`);
    } else if (session.recordingUrl) {
      window.open(session.recordingUrl, "_blank");
    } else {
      toast.info("Recording not available");
    }
  };

  const handleAINotesClick = (session) => {
    navigate(`/dashboard/ai-notetaker/${session.sessionId}`);
  };

  // Paginate sessions
  const paginatedSessions = sessions.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  return (
    <div className="min-h-screen text-white p-3 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4 md:mb-8">
          <h1 className="text-xl md:text-3xl font-bold text-white mb-4 md:mb-6">
            SESSIONS
          </h1>
        </div>

        {/* Sessions Table */}
        <div className="rounded-xl md:rounded-xl border border-gray-600 overflow-hidden primary-card-background">
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-600">
                  <th className="px-4 py-4 text-left text-white font-semibold text-xs">
                    MEETING NAME
                  </th>
                  <th className="px-4 py-4 text-left text-white font-semibold text-xs">
                    AGORA CHANNEL
                  </th>
                  <th className="px-4 py-4 text-left text-white font-semibold text-xs">
                    SESSION ID
                  </th>
                  <th className="px-4 py-4 text-left text-white font-semibold text-xs">
                    DATE
                  </th>
                  <th className="px-4 py-4 text-left text-white font-semibold text-xs">
                    TIME
                  </th>
                  <th className="px-4 py-4 text-left text-white font-semibold text-xs">
                    DURATION
                  </th>
                  <th className="px-4 py-4 text-center text-white font-semibold text-xs">
                    USERS
                  </th>
                  <th className="px-4 py-4 text-center text-white font-semibold text-xs">
                    REPLAY
                  </th>
                  <th className="px-4 py-4 text-center text-white font-semibold text-xs">
                    AI NOTES
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="9"
                      className="px-4 py-8 text-center text-gray-400 text-sm"
                    >
                      Loading sessions...
                    </td>
                  </tr>
                ) : paginatedSessions.length === 0 ? (
                  <tr>
                    <td
                      colSpan="9"
                      className="px-4 py-8 text-center text-gray-400 text-sm"
                    >
                      No sessions found
                    </td>
                  </tr>
                ) : (
                  paginatedSessions.map((session) => (
                    <tr
                      onClick={() => handleSessionClick(session)}
                      key={session.sessionId || session.roomId}
                      className="border-b border-gray-700 transition-colors cursor-pointer hover:bg-gray-700/30"
                    >
                      <td className="px-4 py-3 text-white text-sm max-w-[180px] truncate">
                        {session.custom_session_name ||
                          session.sessionId ||
                          "-"}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-mono text-purple-300 bg-purple-500/10 px-2 py-0.5 rounded">
                          {session.agoraChannel || session.roomId || "-"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-mono text-gray-400">
                          {session.sessionId || "-"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-white text-sm whitespace-nowrap">
                        {session.startDate}
                      </td>
                      <td className="px-4 py-3 text-white text-sm whitespace-nowrap">
                        {session.startTime}
                      </td>
                      <td className="px-4 py-3 text-white text-sm whitespace-nowrap">
                        {session.duration}
                      </td>
                      <td className="px-4 py-3 text-center text-white text-sm">
                        {session.participants?.length || 0}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {session.hasReplay ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReplayClick(session);
                            }}
                            className="inline-flex items-center gap-1 text-green-400 hover:text-green-300 transition-colors text-xs font-medium"
                          >
                            <Play className="w-3.5 h-3.5" />
                            {session.recordingCount > 1
                              ? `${session.recordingCount} Parts`
                              : "View"}
                          </button>
                        ) : (
                          <span className="text-gray-500 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {session.hasAINotes ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAINotesClick(session);
                            }}
                            className="inline-flex items-center gap-1 text-green-400 hover:text-green-300 transition-colors text-xs font-medium"
                          >
                            <FileText className="w-3.5 h-3.5" />
                            Open
                          </button>
                        ) : (
                          <span className="text-gray-500 text-xs">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="block md:hidden p-3">
            {loading ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                Loading sessions...
              </div>
            ) : paginatedSessions.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                No sessions found
              </div>
            ) : (
              <div className="space-y-3">
                {paginatedSessions.map((session) => (
                  <div
                    key={session.sessionId || session.roomId}
                    onClick={() => handleSessionClick(session)}
                    className="bg-gray-700/30 rounded-lg p-3 border border-gray-700 cursor-pointer hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="space-y-2">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="text-white font-medium text-sm mb-1 truncate">
                            {session.custom_session_name ||
                              session.sessionId ||
                              "-"}
                          </div>
                          <div className="text-xs font-mono text-purple-300 bg-purple-500/10 px-2 py-0.5 rounded inline-block">
                            {session.agoraChannel || session.roomId || "-"}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-gray-400">Date: </span>
                          <span className="text-gray-300">{session.startDate || "-"}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Time: </span>
                          <span className="text-gray-300">{session.startTime || "-"}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Duration: </span>
                          <span className="text-gray-300">{session.duration || "-"}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Users: </span>
                          <span className="text-gray-300">{session.participants?.length || 0}</span>
                        </div>
                      </div>
                      <div className="text-xs">
                        <div>
                          <span className="text-gray-400">Session ID: </span>
                          <span className="text-gray-300 font-mono break-all">
                            {session.sessionId || "-"}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-gray-700">
                        <div>
                          {session.hasReplay ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReplayClick(session);
                              }}
                              className="flex items-center gap-1.5 text-green-400 hover:text-green-300 transition-colors text-xs"
                            >
                              <Play className="w-3.5 h-3.5" />
                              {session.recordingCount > 1
                                ? `${session.recordingCount} Parts`
                                : "View Replay"}
                            </button>
                          ) : (
                            <span className="text-gray-500 text-xs">
                              No Replay
                            </span>
                          )}
                        </div>
                        <div>
                          {session.hasAINotes ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAINotesClick(session);
                              }}
                              className="flex items-center gap-1.5 text-green-400 hover:text-green-300 transition-colors text-xs"
                            >
                              <FileText className="w-3.5 h-3.5" />
                              AI Notes
                            </button>
                          ) : (
                            <span className="text-gray-500 text-xs">
                              No AI Notes
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col md:flex-row items-center justify-between px-4 md:px-6 py-3 md:py-4 border-t border-gray-600 gap-3 md:gap-0">
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-xs md:text-sm">
                  Showing{" "}
                  {Math.min((currentPage - 1) * rowsPerPage + 1, totalSessions)}{" "}
                  to {Math.min(currentPage * rowsPerPage, totalSessions)} of{" "}
                  {totalSessions} sessions
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className="p-1.5 md:p-2 rounded-lg bg-[#0000001F] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
                >
                  <ChevronsLeft className="h-4 w-4 md:h-5 md:w-5" />
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-1.5 md:p-2 rounded-lg bg-[#0000001F] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4 md:h-5 md:w-5" />
                </button>
                <span className="text-white text-xs md:text-sm px-2 md:px-4">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-1.5 md:p-2 rounded-lg bg-[#0000001F] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
                >
                  <ChevronRight className="h-4 w-4 md:h-5 md:w-5" />
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className="p-1.5 md:p-2 rounded-lg bg-[#0000001F] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
                >
                  <ChevronsRight className="h-4 w-4 md:h-5 md:w-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sessions;
